import client from './client';
import { ApiResponse, AuthResponse, Cart, Category, Coupon, CouponValidation, DashboardStats, EventDetail, EventSummary, GroupBuy, GroupBuyPhase, GroupBuyStatus, HomeBanner, Order, PageResponse, Product, PurchaseRequest, PurchaseRequestStatus, Review, SiteEvent, TrackingResult, UserCoupon } from '../types';

// Auth
export const authApi = {
  sendEmailCode: (email: string) =>
    client.post<ApiResponse<void>>('/api/auth/email/send-code', { email }),
  register: (data: { email: string; password: string; name: string; phone?: string; code: string }) =>
    client.post<ApiResponse<AuthResponse>>('/api/auth/signup', {
      ...data,
      preferredRegion: 'US',
      preferredLocale: 'EN',
    }),
  login: (data: { email: string; password: string }) =>
    client.post<ApiResponse<AuthResponse>>('/api/auth/login', data),
  socialLogin: (provider: string, idToken: string) =>
    client.post<ApiResponse<AuthResponse>>(`/api/auth/oauth/${provider}`, {
      idToken,
      preferredRegion: 'US',
      preferredLocale: 'EN',
    }),
};

// Products
export const productApi = {
  getList: (params?: { categoryId?: number; keyword?: string; sortType?: string; onSale?: boolean; page?: number; size?: number }) =>
    client.get<ApiResponse<PageResponse<Product>>>('/api/products', { params }),
  getOne: (id: number) =>
    client.get<ApiResponse<Product>>(`/api/products/${id}`),
};

export const eventApi = {
  getList: () => client.get<ApiResponse<EventSummary[]>>('/api/events'),
  getOne: (code: string) => client.get<ApiResponse<EventDetail>>(`/api/events/${code}`),
};

// Categories
export const categoryApi = {
  getAll: () => client.get<ApiResponse<Category[]>>('/api/categories'),
};

// Cart
export const cartApi = {
  get: () => client.get<ApiResponse<Cart>>('/api/cart'),
  addItem: (productId: number, quantity: number, variantId?: number | null) =>
    client.post<ApiResponse<null>>('/api/cart/items', { productId, quantity, variantId: variantId ?? null }),
  updateItem: (productId: number, quantity: number, variantId?: number | null) =>
    client.patch<ApiResponse<null>>(`/api/cart/items/${productId}`, { quantity }, { params: variantId != null ? { variantId } : {} }),
  removeItem: (productId: number, variantId?: number | null) =>
    client.delete<ApiResponse<null>>(`/api/cart/items/${productId}`, { params: variantId != null ? { variantId } : {} }),
  addGroupBuy: (groupBuyId: number, quantity: number, variantId?: number | null) =>
    client.post<ApiResponse<null>>('/api/cart/group-buys', { groupBuyId, quantity, variantId: variantId ?? null }),
  updateGroupBuy: (groupBuyId: number, quantity: number, variantId?: number | null) =>
    client.patch<ApiResponse<null>>(`/api/cart/group-buys/${groupBuyId}`, { quantity }, { params: variantId != null ? { variantId } : {} }),
  removeGroupBuy: (groupBuyId: number, variantId?: number | null) =>
    client.delete<ApiResponse<null>>(`/api/cart/group-buys/${groupBuyId}`, { params: variantId != null ? { variantId } : {} }),
  quote: (items: any[], groupBuyItems: any[]) =>
    client.post<ApiResponse<Cart>>('/api/cart/quote', { items, groupBuyItems }),
  clear: () => client.delete<ApiResponse<Cart>>('/api/cart'),
};

// Orders
export const orderApi = {
  create: (data: any) => client.post<ApiResponse<any>>('/api/orders', data),
  createCart: (data: any) => client.post<ApiResponse<any>>('/api/orders/cart', data),
  capture: (id: number) => client.post<ApiResponse<any>>(`/api/orders/${id}/capture`),
  getMyOrders: (page = 0) =>
    client.get<ApiResponse<PageResponse<Order>>>('/api/orders', { params: { page } }),
  getOne: (id: number) => client.get<ApiResponse<Order>>(`/api/orders/${id}`),
  cancel: (id: number, reason?: string) =>
    client.post<ApiResponse<Order>>(`/api/orders/${id}/cancel`, { reason }),
  requestRefund: (id: number, reason: string) =>
    client.post<ApiResponse<Order>>(`/api/orders/${id}/refund-request`, { reason }),
  getTracking: (id: number) =>
    client.get<ApiResponse<TrackingResult>>(`/api/orders/${id}/tracking`),
};

// Stripe
export const stripeApi = {
  getConfig: () => client.get<ApiResponse<{ publishableKey: string }>>('/api/payment/stripe/config'),
  createPaymentIntent: (data: {
    groupBuyId: number;
    quantity: number;
    userCouponId?: number;
    usedPoints?: number;
  }) =>
    client.post<ApiResponse<{ paymentIntentId: string; clientSecret: string; amountUsd: number }>>(
      '/api/payment/stripe/create-payment-intent', data
    ),
  confirm: (data: {
    stripePaymentIntentId: string;
    groupBuyId: number;
    variantId?: number | null;
    quantity: number;
    shippingAddress: {
      recipientName: string;
      country: string;
      postalCode: string;
      line1: string;
      line2?: string;
      city: string;
      state: string;
      phone: string;
    };
    userCouponId?: number;
    usedPoints?: number;
  }) => client.post<ApiResponse<Order>>('/api/payment/stripe/confirm', data),
};

// PayPal
export const payPalApi = {
  quote: (amount: number) =>
    client.get<ApiResponse<{ krw: number; usd: number; ratePerUsd: number }>>(
      '/api/payment/paypal/quote', { params: { amount } }
    ),
  createOrder: (amount: number) =>
    client.post<ApiResponse<{ id: string }>>('/api/payment/paypal/create-order', { amount }),
  capture: (data: {
    paypalOrderId: string;
    items: { productId: number; quantity: number }[];
    receiverName: string;
    receiverPhone: string;
    zipCode: string;
    address: string;
    addressDetail?: string;
    memo?: string;
    couponCode?: string;
  }) => client.post<ApiResponse<Order>>('/api/payment/paypal/capture', data),
};

// Reviews
export const reviewApi = {
  getByProduct: (productId: number, page = 0, sort: string = 'id,desc') =>
    client.get<ApiResponse<any>>(`/api/reviews/products/${productId}`, { params: { page, sort } }),
  create: (data: {
    productId: number;
    orderItemId?: number;
    rating: number;
    content: string;
    images?: Array<{ url: string; displayOrder: number }>;
  }) => client.post<ApiResponse<Review>>('/api/reviews', data),
  delete: (id: number) => client.delete<ApiResponse<null>>(`/api/reviews/${id}`),
  uploadImage: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return client.post<ApiResponse<string>>('/api/upload/review-image', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Points
export const pointApi = {
  getMyPoints: (page = 0) =>
    client.get<ApiResponse<{ balance: number; balanceUsd: number; transactions: { content: any[]; totalPages: number; totalElements: number } }>>(
      '/api/users/me/points', { params: { page } }
    ),
};

// Group Buy (public)
export const groupBuyApi = {
  getList: (phase?: GroupBuyPhase, page = 0, categoryId?: number | null, size?: number) =>
    client.get<ApiResponse<PageResponse<GroupBuy>>>('/api/group-buys', { params: { phase, page, categoryId: categoryId ?? undefined, size } }),
  getOne: (code: string) =>
    client.get<ApiResponse<GroupBuy>>(`/api/group-buys/${code}`),
};

// Group Buy (admin)
export const adminGroupBuyApi = {
  getList: (status?: GroupBuyStatus, page = 0) =>
    client.get<ApiResponse<PageResponse<GroupBuy>>>('/api/admin/group-buys', { params: { status, page } }),
  getOne: (id: number) =>
    client.get<ApiResponse<GroupBuy>>(`/api/admin/group-buys/${id}`),
  create: (data: any) =>
    client.post<ApiResponse<GroupBuy>>('/api/admin/group-buys', data),
  update: (id: number, data: any) =>
    client.put<ApiResponse<GroupBuy>>(`/api/admin/group-buys/${id}`, data),
  delete: (id: number) =>
    client.delete<ApiResponse<null>>(`/api/admin/group-buys/${id}`),
  updateStatus: (id: number, status: GroupBuyStatus) =>
    client.patch<ApiResponse<GroupBuy>>(`/api/admin/group-buys/${id}/status`, null, { params: { status } }),
};

// Coupons
export const couponApi = {
  getMyCoupons: () => client.get<ApiResponse<UserCoupon[]>>('/api/coupons/my'),
  validate: (userCouponId: number, amount: number) =>
    client.post<ApiResponse<{ discount: number }>>('/api/coupons/validate', { userCouponId, amount }),
  issueCoupon: (couponId: number, userId: number) =>
    client.post<ApiResponse<UserCoupon>>(`/api/admin/coupons/${couponId}/issue`, { userId }),
  issueCouponByEmail: (couponId: number, email: string) =>
    client.post<ApiResponse<UserCoupon>>(`/api/admin/coupons/${couponId}/issue`, { email }),
  getWelcomeStatus: () => client.get<ApiResponse<any>>('/api/coupons/welcome/status'),
  claimWelcome: () => client.post<ApiResponse<UserCoupon>>('/api/coupons/welcome/claim'),
};

// Exchange rate
export const exchangeApi = {
  get: () => client.get<ApiResponse<{ krwPerUsd: number }>>('/api/exchange-rate'),
};

// Site events (admin-managed announcement events)
export const siteEventApi = {
  getList: () => client.get<ApiResponse<SiteEvent[]>>('/api/site-events'),
  getOne: (id: number) => client.get<ApiResponse<SiteEvent>>(`/api/site-events/${id}`),
};

export const adminSiteEventApi = {
  getList: () => client.get<ApiResponse<SiteEvent[]>>('/api/admin/site-events'),
  create: (data: any) => client.post<ApiResponse<SiteEvent>>('/api/admin/site-events', data),
  update: (id: number, data: any) => client.put<ApiResponse<SiteEvent>>(`/api/admin/site-events/${id}`, data),
  delete: (id: number) => client.delete<ApiResponse<void>>(`/api/admin/site-events/${id}`),
};

// Upload
export const uploadApi = {
  image: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return client.post<ApiResponse<string>>('/api/admin/upload/image', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Admin
export const adminApi = {
  getDashboard: () => client.get<ApiResponse<DashboardStats>>('/api/admin/stats/overview'),
  getOrders: (page = 0) =>
    client.get<ApiResponse<PageResponse<any>>>('/api/admin/orders', { params: { page } }),
  updateOrderStatus: (id: number, status: string) =>
    client.patch<ApiResponse<any>>(`/api/admin/orders/${id}/status`, null, { params: { status } }),
  registerTracking: (id: number, trackingNumber: string, packingVideoUrl?: string) =>
    client.patch<ApiResponse<any>>(`/api/admin/orders/${id}/tracking`, { trackingNumber, packingVideoUrl }),
  getUsers: (page = 0) =>
    client.get<ApiResponse<any>>('/api/admin/users', { params: { page } }),
  changeUserStatus: (id: number, status: string) =>
    client.patch<ApiResponse<any>>(`/api/admin/users/${id}/status`, null, { params: { status } }),
  changeUserGrade: (id: number, grade: string) =>
    client.patch<ApiResponse<any>>(`/api/admin/users/${id}/grade`, null, { params: { grade } }),
  createProduct: (data: any) =>
    client.post<ApiResponse<any>>('/api/admin/products', data),
  updateProduct: (id: number, data: any) =>
    client.put<ApiResponse<any>>(`/api/admin/products/${id}`, data),
  deleteProduct: (id: number) => client.delete<ApiResponse<null>>(`/api/admin/products/${id}`),
  getAdminCategories: () =>
    client.get<ApiResponse<Category[]>>('/api/admin/categories'),
  createCategory: (data: {
    name: string; nameEn?: string;
    description?: string; descriptionEn?: string;
    parentId?: number;
  }) => client.post<ApiResponse<Category>>('/api/admin/categories', data),
  updateCategory: (id: number, data: {
    name: string; nameEn?: string;
    description?: string; descriptionEn?: string;
    parentId?: number;
  }) => client.put<ApiResponse<Category>>(`/api/admin/categories/${id}`, data),
  deleteCategory: (id: number) => client.delete<ApiResponse<null>>(`/api/admin/categories/${id}`),
  getExchangeRates: () =>
    client.get<ApiResponse<{ krwPerUsd: number; krwPerJpy: number; usdPerKrw: number; jpyPerKrw: number }>>(
      '/api/admin/exchange-rates'
    ),
  getProducts: (page = 0, size = 20) =>
    client.get<ApiResponse<PageResponse<Product>>>('/api/admin/products', { params: { page, size } }),
  getCoupons: (page = 0) =>
    client.get<ApiResponse<PageResponse<Coupon>>>('/api/admin/coupons', { params: { page } }),
  createCoupon: (data: {
    code: string; name: string; discountType: 'FIXED' | 'PERCENTAGE';
    discountValue: number; minOrderAmount?: number; maxDiscountAmount?: number; expiresAt?: string;
  }) => client.post<ApiResponse<Coupon>>('/api/admin/coupons', data),
  deleteCoupon: (id: number) => client.delete<ApiResponse<null>>(`/api/admin/coupons/${id}`),
  toggleCoupon: (id: number) => client.patch<ApiResponse<Coupon>>(`/api/admin/coupons/${id}/toggle`),
  getHomeBanners: () => client.get<ApiResponse<HomeBanner[]>>('/api/admin/home-banner'),
  createHomeBanner: (data: HomeBannerInput) =>
    client.post<ApiResponse<HomeBanner>>('/api/admin/home-banner', data),
  updateHomeBanner: (id: number, data: HomeBannerInput) =>
    client.put<ApiResponse<HomeBanner>>(`/api/admin/home-banner/${id}`, data),
  deleteHomeBanner: (id: number) =>
    client.delete<ApiResponse<null>>(`/api/admin/home-banner/${id}`),
};

export interface HomeBannerInput {
  imageUrl: string | null;
  categoryId: number | null;
  displayOrder: number;
  enabled: boolean;
}

// 홈 메인 배너 (공개) — 노출 배너 목록을 순서대로 반환
export const homeBannerApi = {
  get: () => client.get<ApiResponse<HomeBanner[]>>('/api/home-banner'),
};

// 대리구매(구매대행) 신청 — 유저
export const purchaseRequestApi = {
  create: (data: {
    productUrl: string;
    productName: string;
    optionText?: string;
    quantity: number;
    budgetUsd?: number;
    memo?: string;
  }) => client.post<ApiResponse<PurchaseRequest>>('/api/purchase-requests', data),
  getMine: (page = 0) =>
    client.get<ApiResponse<PageResponse<PurchaseRequest>>>('/api/purchase-requests', { params: { page } }),
  cancel: (id: number) =>
    client.post<ApiResponse<PurchaseRequest>>(`/api/purchase-requests/${id}/cancel`),
  createPayPalOrder: (id: number) =>
    client.post<ApiResponse<{ paypalOrderId: string }>>(`/api/purchase-requests/${id}/paypal/create-order`),
  capturePayPal: (id: number, paypalOrderId: string) =>
    client.post<ApiResponse<PurchaseRequest>>(`/api/purchase-requests/${id}/paypal/capture`, null, { params: { paypalOrderId } }),
  createExtraPayPalOrder: (id: number) =>
    client.post<ApiResponse<{ paypalOrderId: string }>>(`/api/purchase-requests/${id}/extra/paypal/create-order`),
  captureExtraPayPal: (id: number, paypalOrderId: string) =>
    client.post<ApiResponse<PurchaseRequest>>(`/api/purchase-requests/${id}/extra/paypal/capture`, null, { params: { paypalOrderId } }),
};

// 대리구매 신청 — 어드민
export const adminPurchaseRequestApi = {
  getList: (page = 0) =>
    client.get<ApiResponse<PageResponse<PurchaseRequest>>>('/api/admin/purchase-requests', { params: { page } }),
  quote: (id: number, data: { itemPriceUsd: number; serviceFeeUsd: number; shippingFeeUsd: number; adminNote?: string }) =>
    client.post<ApiResponse<PurchaseRequest>>(`/api/admin/purchase-requests/${id}/quote`, data),
  updateStatus: (id: number, status: PurchaseRequestStatus, adminNote?: string) =>
    client.patch<ApiResponse<PurchaseRequest>>(`/api/admin/purchase-requests/${id}/status`, null, {
      params: { status, adminNote },
    }),
  registerTracking: (id: number, trackingNumber: string, packingVideoUrl: string) =>
    client.post<ApiResponse<PurchaseRequest>>(`/api/admin/purchase-requests/${id}/tracking`, { trackingNumber, packingVideoUrl }),
  addExtraCharge: (id: number, amountUsd: number, reason: string) =>
    client.post<ApiResponse<PurchaseRequest>>(`/api/admin/purchase-requests/${id}/extra-charge`, { amountUsd: String(amountUsd), reason }),
};
